/**
 * Graphics intake — the PUBLIC customer page (GI-PAGE, Pete 2026-11-04).
 * Route: /graphics-intake/<orderId>?k=<sig> — served WITHOUT login; the `k`
 * HMAC link signature (minted by the API's send route) is the only
 * credential. Submissions POST to /api/v3/graphicsIntake/:orderId?k= and land
 * in the order's Graphics Notes (shared with Konstner).
 *
 * Pete's 2026-11-04 revision: QUESTION ONE is the customer's concept in their
 * own words — a big free-text box with the file upload attached to it — and
 * everything after it is optional and light. One calm scroll, Wagner brand
 * (#00567D on #F5F2EB).
 */
import { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress,
  Container, Divider, Stack, TextField, Typography,
} from '@mui/material';

const API_URL = (import.meta.env.VITE_WAGNER_API_URL || '').replace(/\/$/, '');
type RecordData = Record<string, any>;

const VIBES = [
  'Mountain / nature scenes', 'Patterns / geometry', 'Logos & personal marks',
  'Bold / minimal', 'Retro / vintage', 'Something unique',
];

export interface IntakePageProps {
  orderId: number;
  sig: string;
  api?: (path: string, init?: RequestInit) => Promise<any>;
}

export function GraphicsIntakePage({ orderId, sig, api }: IntakePageProps) {
  const call = api || ((path: string, init?: RequestInit) =>
    fetch(`${API_URL}${path}`, { ...init, headers: { 'Content-Type': 'application/json' } })
      .then(async (r) => ({ ok: r.ok, status: r.status, body: await r.json().catch(() => ({})) })));

  const [context, setContext] = useState<RecordData | null>(null);
  const [loading, setLoading] = useState(true);
  const [fatal, setFatal] = useState<string | null>(null);
  const [concept, setConcept] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [vibe, setVibe] = useState<string[]>([]);
  const [mustAppear, setMustAppear] = useState('');
  const [avoid, setAvoid] = useState('');
  const [colors, setColors] = useState('');
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await call(`/api/v3/graphicsIntake/${orderId}?k=${encodeURIComponent(sig)}`);
        if (!r.ok) { setFatal(r.status === 403 ? 'This link is not valid.' : 'We could not find this order.'); return; }
        setContext(r.body);
      } catch { setFatal('Connection problem — please try again.'); }
      finally { setLoading(false); }
    })();
  }, [orderId, sig]);

  const readFileBase64 = (file: File) => new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => { const s = String(reader.result || ''); resolve(s.includes(',') ? s.slice(s.indexOf(',') + 1) : s); };
    reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
    reader.readAsDataURL(file);
  });

  const addFiles = (list: FileList | null) => {
    if (!list) return;
    setFiles((p) => [...p, ...Array.from(list)].slice(0, 10));
    if (fileRef.current) fileRef.current.value = '';
  };

  const hasAnyInput = Boolean(concept.trim() || files.length || vibe.length || mustAppear.trim() || avoid.trim() || colors.trim());

  const submit = async () => {
    if (sending || !hasAnyInput) return;
    setSending(true); setError(null);
    try {
      const images = [];
      for (const f of files) images.push({ filename: f.name, base64: await readFileBase64(f) });
      const r = await call(`/api/v3/graphicsIntake/${orderId}?k=${encodeURIComponent(sig)}`, {
        method: 'POST',
        body: JSON.stringify({
          answers: {
            text: concept.trim() || undefined,
            vibe: vibe.join(', ') || undefined,
            mustAppear: mustAppear || undefined,
            avoid: avoid || undefined,
            colors: colors || undefined,
          },
          images,
        }),
      });
      if (!r.ok) { setError(r.body?.error || 'Sending failed — please try again.'); return; }
      setDone(true);
    } catch { setError('Sending failed — please try again.'); }
    finally { setSending(false); }
  };

  if (loading) return <Centered><CircularProgress /></Centered>;
  if (fatal) return <Centered><Card><CardContent><Alert severity="error">{fatal}</Alert>
    <Typography sx={{ mt: 2 }}>Questions? Reply to the email you received or contact Wagner Custom Skis.</Typography>
  </CardContent></Card></Centered>;

  const first = (context?.skierName || '').split(' ')[0] || 'there';

  if (done) return (
    <Centered>
      <Card><CardContent>
        <Typography variant="h5" sx={{ color: '#00567D', fontWeight: 650 }}>Thank you, {first}!</Typography>
        <Typography sx={{ mt: 2 }}>Your ideas are now with our ski designers{files.length ? `, along with your ${files.length} file${files.length > 1 ? 's' : ''}` : ''}. Someone from the Wagner team will follow up with you as your ski takes shape.</Typography>
        <Alert severity="info" sx={{ mt: 2 }}>Have more ideas later? Just reopen this link from your email — you can add or change things anytime.</Alert>
      </CardContent></Card>
    </Centered>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F5F2EB', py: { xs: 3, md: 5 } }}>
      <Container maxWidth="sm">
        <Typography sx={{ color: '#00567D', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, fontSize: 14 }}>Wagner Custom Skis</Typography>
        <Typography variant="h4" sx={{ color: '#454545', fontWeight: 700, mt: 0.5 }}>Your graphics. Your skis.</Typography>
        <Typography sx={{ mt: 2, color: '#666565' }}>
          Hi {first} — this pair ({context?.orderName || 'your skis'}) is about to get its look.
          Tell us what you want in your own words, attach anything that inspires you, and we&apos;ll take it from there.
          Nothing here is final — the only required thing is what YOU want.
        </Typography>

        <Stack spacing={3} sx={{ mt: 3 }}>
          {/* QUESTION ONE — the concept, in the customer's words, files attached */}
          <Card sx={{ border: '2px solid #00567D', boxShadow: '0 2px 8px rgba(0,86,125,0.10)' }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Chip size="small" label="1" sx={{ bgcolor: '#00567D', color: '#fff', fontWeight: 700 }} />
                <Typography fontWeight={650} sx={{ color: '#454545' }}>Tell us your concept — in your own words</Typography>
              </Stack>
              <Typography variant="body2" sx={{ color: '#666565', mb: 1.5 }}>
                Describe what you want to see on your ski. A scene, a feeling, a memory, a list of things you love —
                however it comes out is exactly what we want.
              </Typography>
              <TextField
                fullWidth multiline minRows={6}
                value={concept}
                onChange={(e) => setConcept(e.target.value)}
                placeholder={'e.g. "I ski the La Sal ridge at sunrise every spring — I\'d love the ski to feel like that morning: deep blues fading to gold, jagged peaks, my kids\' initials near the tail."'}
              />
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
                <Button variant="outlined" component="label" sx={{ borderColor: '#00567D', color: '#00567D' }}>
                  Attach files
                  <input type="file" multiple hidden ref={fileRef} accept="image/*,.pdf"
                    onChange={(e) => addFiles(e.target.files)} />
                </Button>
                <Typography variant="caption" sx={{ color: '#666565' }}>
                  Photos, screenshots, sketches, logos, PDFs — up to 10
                </Typography>
              </Stack>
              {files.length > 0 && (
                <Stack spacing={0.75} sx={{ mt: 1.5 }}>
                  {files.map((f, i) => (
                    <Stack key={`${f.name}-${i}`} direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" sx={{ color: '#454545' }}>{f.name}</Typography>
                      <Button size="small" color="error" onClick={() => setFiles((p) => p.filter((_, j) => j !== i))}>Remove</Button>
                    </Stack>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>

          {/* Optional light follow-ups */}
          <Card><CardContent>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Chip size="small" variant="outlined" label="2 · optional" />
              <Typography fontWeight={650} sx={{ color: '#454545' }}>Any styles that appeal to you?</Typography>
            </Stack>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
              {VIBES.map((v) => (
                <Chip key={v} label={v} clickable color={vibe.includes(v) ? 'primary' : 'default'}
                  variant={vibe.includes(v) ? 'filled' : 'outlined'}
                  onClick={() => setVibe((cur) => cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v])} />
              ))}
            </Stack>
          </CardContent></Card>

          <Card><CardContent>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Chip size="small" variant="outlined" label="3 · optional" />
              <Typography fontWeight={650} sx={{ color: '#454545' }}>Anything that must appear — or be avoided?</Typography>
            </Stack>
            <Stack spacing={1.5}>
              <TextField fullWidth multiline minRows={2} value={mustAppear} onChange={(e) => setMustAppear(e.target.value)}
                placeholder="Must appear: e.g. my company logo, 'Wagner' near the tail" />
              <TextField fullWidth multiline minRows={2} value={avoid} onChange={(e) => setAvoid(e.target.value)}
                placeholder="Avoid: e.g. no neon, no cartoon characters" />
            </Stack>
          </CardContent></Card>

          <Card><CardContent>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Chip size="small" variant="outlined" label="4 · optional" />
              <Typography fontWeight={650} sx={{ color: '#454545' }}>Favorite colors?</Typography>
            </Stack>
            <TextField fullWidth value={colors} onChange={(e) => setColors(e.target.value)}
              placeholder="Roughly is fine — e.g. deep blues and silver" />
          </CardContent></Card>

          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
          <Button variant="contained" size="large" sx={{ bgcolor: '#00567D', py: 1.5, fontSize: 17 }}
            disabled={sending || !hasAnyInput}
            onClick={() => { void submit(); }}>
            {sending ? <CircularProgress size={24} color="inherit" /> : 'Send my ideas to Wagner'}
          </Button>
          <Divider />
          <Typography variant="caption" sx={{ color: '#666565' }}>
            Your ideas go directly to our ski design team. We may reach out to talk through the design before your skis are built.
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: '#F5F2EB', p: 3 }}>{children}</Box>;
}

/** Parse a location into intake route args, or null when this isn't the intake route. */
export const parseIntakeLocation = (pathname: string, search: string): { orderId: number; sig: string } | null => {
  const m = pathname.match(/^\/graphics-intake\/(\d+)\/?$/);
  if (!m) return null;
  const sig = new URLSearchParams(search).get('k') || '';
  if (!sig) return null;
  return { orderId: Number(m[1]), sig };
};